import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Input, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { FJ170, FJ171, j207Api } from '../../services/j207Api';
import { BatchAcceptanceBridge, pwj207f1AcceptanceManifest } from '../../features/acceptance';
import { useJ207Mode } from './hooks/useJ207Mode';
import { useJ207Record } from './hooks/useJ207Record';
import { useJ207Navigation } from './hooks/useJ207Navigation';
import { SharedActionBar } from '../../components/SharedActionBar';
import { useModulePermission } from '../../hooks/usePermission';
import './J207Form.css';

const dateToInput = (v: FJ170[keyof FJ170]): string => {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.substring(0, 10) : s;
};

const J207Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');

  const { mode, setMode, isEditing } = useJ207Mode();
  const {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  } = useJ207Record();

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useJ207Navigation(
    useCallback(async (id: string) => { await loadRecord(id); }, [loadRecord]),
  );

  const { canCreate, canEdit, canDelete } = useModulePermission('pwj207f1');

  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load J207 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- QuickSearch (client-side) ---
  const handleSearch = useCallback(() => {
    const keyword = searchText.trim();
    if (!keyword) return;
    const idx = allIds.findIndex(id => id.includes(keyword));
    if (idx >= 0) {
      setCurrentIdx(idx);
      loadRecord(allIds[idx]);
    }
  }, [searchText, allIds, setCurrentIdx, loadRecord]);

  // --- CRUD handlers (split for SharedActionBar) ---
  const handleAdd = useCallback(() => {
    setMode('add');
    resetForAdd();
  }, [setMode, resetForAdd]);

  const handleEdit = useCallback(() => {
    setMode('edit');
  }, [setMode]);

  const handleSaveAction = useCallback(async () => {
    await handleSave(mode, async (result: FJ170) => {
      if (mode === 'add') {
        const ids = await loadList();
        const newIdx = ids.indexOf(result.fj170_01);
        setCurrentIdx(newIdx >= 0 ? newIdx : 0);
      }
      applyResult(result);
      setMode('browse');
    });
  }, [mode, handleSave, loadList, setCurrentIdx, applyResult, setMode]);

  const handleCancel = useCallback(() => {
    revertToRecord();
    setMode('browse');
  }, [revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除領用單 ${record?.fj170_01} 嗎？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecord(ids[0]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  const handleDetailChange = useCallback((idx: number, key: keyof FJ171, value: FJ171[keyof FJ171]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, [setDetails]);

  // Currently selected detail row for below-grid info
  const [selectedDetailIdx, setSelectedDetailIdx] = useState<number>(0);
  const selectedDetail = details[selectedDetailIdx] || null;

  // Approval status
  const isApproved = record?.fj170_80 === 'P';
  const statusLabel = isApproved ? '已核准' : record?.fj170_01 ? '未核准' : null;

  return (
    <div className="j207-form">
      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <h1>研發領用單 (pwj207f1)</h1>
          </div>
          <div className="page-title-right">
            <span style={{ fontSize: 12, color: '#999' }}>
              {record?.fj170_01 || '(新增)'}
            </span>
          </div>
        </div>

        {/* Form-level fields — FoxPro two-row header layout */}
        <div className="form-content">
          {/* Row 1: 領用單號 (left) | 領用日期 (right) — matching FoxPro positions */}
          <div className="header-row">
            <div className="header-field">
              <label>領用單號</label>
              <input type="text" readOnly value={getField('fj170_01') as string || ''} style={{ width: 100 }} data-contract-field="fj170_01" />
            </div>
            <div className="header-field">
              <label className="required">領用日期</label>
              <input
                type="date"
                disabled={!isEditing}
                value={dateToInput(getField('fj170_11'))}
                onChange={e => setField('fj170_11', e.target.value || null)}
                style={{ width: 150 }} data-contract-field="fj170_11"
              />
            </div>
          </div>
          {/* Row 2: 說明 (left, wide) | 托工單號 (right) */}
          <div className="header-row" style={{ marginTop: 4 }}>
            <div className="header-field" style={{ flex: 1 }}>
              <label data-contract-field="fj170_13">說明</label>
              <input
                type="text"
                maxLength={100}
                disabled={!isEditing}
                value={getField('fj170_13') as string || ''}
                onChange={e => setField('fj170_13', e.target.value)}
                style={{ width: '100%', maxWidth: 500 }}
                data-contract-field="fj170_13"
              />
            </div>
            <div className="header-field">
              <label data-contract-field="fj170_10">托工單號</label>
              <input
                type="text"
                readOnly
                value={getField('fj170_10') as string || ''}
                style={{ width: 100 }}
                data-contract-field="fj170_10"
              />
            </div>
            <div className="header-field">
              <label data-contract-field="fj170_80">核准狀態</label>
              <span>{statusLabel || '草稿'}</span>
            </div>
          </div>

          {/* QuickSearch — FoxPro Pwtqseek1 */}
          <div style={{ display: 'flex', gap: 8, margin: '8px 0' }}>
            <Input
              placeholder="領用單號查詢"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              disabled={isEditing}
              style={{ width: 200 }}
              allowClear
            />
            <Button
              icon={<SearchOutlined />}
              onClick={handleSearch}
              disabled={isEditing}
            >
              查詢
            </Button>
          </div>

          {/* ── Grid (fj171) ── */}
          <div className="grid-toolbar">
            <span className="grid-count">總項次: {details.length}</span>
            {isEditing && (
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addDetailRow}>
                明細新增
              </Button>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="detail-table">
              <thead>
                <tr>
                  <th style={{ width: 45 }} data-contract-field="fj171_02">項次</th>
                  <th style={{ width: 80 }} data-contract-field="fj171_12">庫位</th>
                  <th style={{ width: 90 }} data-contract-field="fj171_10">產品大類</th>
                  <th style={{ width: 90 }} data-contract-field="fj171_11">產品細類</th>
                  <th style={{ width: 190 }} data-contract-field="fj171_13">批號</th>
                  <th style={{ width: 110 }} data-contract-field="fj171_15">領用量</th>
                  <th style={{ width: 55 }} data-contract-field="fj171_14">單位</th>
                  {isEditing && <th style={{ width: 50 }} data-acceptance-non-field="row_actions">操作</th>}
                </tr>
              </thead>
              <tbody>
                {details.length === 0 && (
                  <tr>
                    <td colSpan={isEditing ? 8 : 7} style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                      尚無明細資料
                    </td>
                  </tr>
                )}
                {details.map((d, idx) => (
                  <tr
                    key={idx}
                    className={idx === selectedDetailIdx ? 'row-selected' : ''}
                    onClick={() => setSelectedDetailIdx(idx)}
                  >
                    <td style={{ textAlign: 'center' }}>{d.fj171_02 ?? idx + 1}</td>
                    <td>
                      <input
                        type="text" maxLength={5}
                        disabled={!isEditing}
                        value={d.fj171_12 || ''}
                        onChange={e => handleDetailChange(idx, 'fj171_12', e.target.value)}
                        style={{ width: '100%' }} data-contract-field="fj171_12"
                      />
                    </td>
                    <td>
                      <input
                        type="text" maxLength={6}
                        disabled={!isEditing}
                        value={d.fj171_10 || ''}
                        onChange={e => handleDetailChange(idx, 'fj171_10', e.target.value)}
                        style={{ width: '100%' }}
                        data-contract-field="fj171_10"
                      />
                    </td>
                    <td>
                      <input
                        type="text" maxLength={6}
                        disabled={!isEditing}
                        value={d.fj171_11 || ''}
                        onChange={e => handleDetailChange(idx, 'fj171_11', e.target.value)}
                        style={{ width: '100%' }}
                        data-contract-field="fj171_11"
                      />
                    </td>
                    <td>
                      <input
                        type="text" maxLength={17}
                        disabled={!isEditing}
                        value={d.fj171_13 || ''}
                        onChange={e => handleDetailChange(idx, 'fj171_13', e.target.value)}
                        style={{ width: '100%' }} data-contract-field="fj171_13"
                      />
                    </td>
                    <td>
                      <input
                        type="number" step="0.01"
                        disabled={!isEditing}
                        value={d.fj171_15 ?? 0}
                        onChange={e => handleDetailChange(idx, 'fj171_15', parseFloat(e.target.value) || 0)}
                        style={{ width: '100%' }} data-contract-field="fj171_15"
                      />
                    </td>
                    <td>
                      <input
                        type="text" maxLength={4}
                        disabled={!isEditing}
                        value={d.fj171_14 || ''}
                        onChange={e => handleDetailChange(idx, 'fj171_14', e.target.value)}
                        style={{ width: '100%' }}
                        data-contract-field="fj171_14"
                      />
                    </td>
                    {isEditing && (
                      <td>
                        <Popconfirm title="確定刪除此明細?" onConfirm={() => deleteDetailRow(idx)}>
                          <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Below-grid detail info (read-only product spec from f113) ── */}
          {selectedDetail && (
            <div className="detail-info-area">
              <div className="detail-info-left">
                <div className="info-row">
                  <label>庫位:</label>
                  <span>{selectedDetail.fj171_12 || ''}</span>
                </div>
                <div className="info-row">
                  <label>顏色:</label>
                  <span>{selectedDetail.fj171_18 || ''}</span>
                </div>
                <div className="info-row">
                  <label>產品規格</label>
                </div>
                <textarea
                  className="spec-textarea"
                  readOnly
                  value={''}
                  rows={3}
                />
              </div>
              <div className="detail-info-mid">
                <fieldset className="info-group">
                  <legend>幅寬</legend>
                  <div className="info-row">
                    <label>針孔內</label>
                    <span className="num-value">{selectedDetail.f113_22 ?? ''}</span>
                  </div>
                  <div className="info-row">
                    <label>針孔外</label>
                    <span className="num-value">{selectedDetail.f113_26 ?? ''}</span>
                  </div>
                </fieldset>
                <fieldset className="info-group">
                  <legend>重量</legend>
                  <div className="info-row">
                    <label>碼重</label>
                    <span className="num-value">{selectedDetail.f113_24 ?? ''}</span>
                  </div>
                  <div className="info-row">
                    <label>米平方重</label>
                    <span className="num-value">{selectedDetail.f113_241 ?? ''}</span>
                  </div>
                </fieldset>
              </div>
            </div>
          )}
        </div>

        {/* Spacer for fixed bottom bar */}
        <div style={{ height: 64 }} />

        {/* SharedActionBar — replaces page-local ActionBar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => navTo(0, isEditing)}
          onNavPrev={() => navTo(currentIdx - 1, isEditing)}
          onNavNext={() => navTo(currentIdx + 1, isEditing)}
          onNavLast={() => navTo(allIds.length - 1, isEditing)}
          onAdd={handleAdd}
          onModify={handleEdit}
          onDelete={handleDeleteAction}
          onSave={handleSaveAction}
          onCancel={handleCancel}
          onExit={() => navigate('/erp')}
          canCreate={canCreate}
          canEdit={canEdit && !isApproved}
          canDelete={canDelete && !isApproved}
          statusLabel={statusLabel}
        />
        <BatchAcceptanceBridge
          manifest={pwj207f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fj170_01 ? [record.fj170_01] : []}
          keyOf={(item) => item.fj170_01}
          queryExact={(key) => j207Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && navTo(0, false)}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />
      </div>
    </div>
  );
};

export default J207Form;
